// Features2.js - Contains functions for creating an advanced features section with tabs
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates an advanced Features section with tabs, statistics, and visual elements
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the features section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The features section elements
 */
export const createFeaturesElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const featuresSectionClass = 'features2-section';
  const featuresContainerClass = 'features2-container';
  const featuresHeaderClass = 'features2-header';
  const featuresTitleClass = 'features2-title';
  const featuresSubtitleClass = 'features2-subtitle';
  const featuresContentClass = 'features2-content';
  const featuresTabsClass = 'features2-tabs';
  const featuresTabClass = 'features2-tab';
  const featuresTabActiveClass = 'features2-tab-active';
  const featuresTabsContentClass = 'features2-tabs-content';
  const featuresTabContentClass = 'features2-tab-content';
  const featuresTabContentActiveClass = 'features2-tab-content-active';
  const featuresImageClass = 'features2-image';
  const featuresInfoClass = 'features2-info';
  const featuresInfoTitleClass = 'features2-info-title';
  const featuresInfoDescriptionClass = 'features2-info-description';
  const featuresChecklistClass = 'features2-checklist';
  const featuresChecklistItemClass = 'features2-checklist-item';
  const featuresCheckIconClass = 'features2-check-icon';
  const featuresStatsClass = 'features2-stats';
  const featuresStatClass = 'features2-stat';
  const featuresStatNumberClass = 'features2-stat-number';
  const featuresStatLabelClass = 'features2-stat-label';
  const featuresCTAClass = 'features2-cta';
  const featuresCTAButtonClass = 'features2-cta-button';
  
  // Define styles
  // Desktop Styles (default)
  const featuresSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f9fafb',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    overflow: 'hidden'
  };

  const featuresContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const featuresHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '50px',
    maxWidth: '800px'
  };

  const featuresTitleStyles = {
    fontSize: '48px',
    fontWeight: '800',
    marginBottom: '20px',
    color: '#111827',
    lineHeight: '1.2'
  };

  const featuresSubtitleStyles = {
    fontSize: '20px',
    lineHeight: '1.6',
    color: '#4b5563',
    maxWidth: '700px'
  };

  const featuresContentStyles = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '30px'
  };

  const featuresTabsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '10px',
    marginBottom: '40px',
    flexWrap: 'wrap',
    width: '100%'
  };

  const featuresTabStyles = {
    padding: '14px 24px',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#4b5563',
    backgroundColor: '#ffffff',
    border: '1px solid #e5e7eb',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)'
  };

  const featuresTabActiveStyles = {
    color: '#ffffff',
    backgroundColor: '#5b42b5',
    borderColor: '#5b42b5',
    boxShadow: '0 4px 10px rgba(91, 66, 181, 0.3)'
  };

  const featuresTabsContentStyles = {
    width: '100%',
    position: 'relative'
  };

  const featuresTabContentStyles = {
    display: 'none',
    opacity: '0',
    transition: 'opacity 0.3s ease',
    width: '100%'
  };

  const featuresTabContentActiveStyles = {
    display: 'flex',
    opacity: '1',
    flexDirection: 'row',
    gap: '60px',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const featuresImageStyles = {
    width: '48%',
    borderRadius: '12px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    objectFit: 'cover',
    objectPosition: 'center'
  };

  const featuresInfoStyles = {
    width: '48%',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const featuresInfoTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#111827',
    marginBottom: '16px',
    lineHeight: '1.3'
  };

  const featuresInfoDescriptionStyles = {
    fontSize: '18px',
    lineHeight: '1.7',
    color: '#4b5563',
    marginBottom: '24px'
  };

  const featuresChecklistStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginTop: '8px',
    marginBottom: '32px'
  };

  const featuresChecklistItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    fontSize: '16px',
    lineHeight: '1.5',
    color: '#4b5563'
  };

  const featuresCheckIconStyles = {
    width: '20px',
    height: '20px',
    minWidth: '20px',
    color: '#5b42b5',
    marginTop: '3px'
  };

  const featuresStatsStyles = {
    display: 'flex',
    gap: '40px',
    marginTop: '20px',
    marginBottom: '32px'
  };

  const featuresStatStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  };

  const featuresStatNumberStyles = {
    fontSize: '36px',
    fontWeight: '800',
    color: '#5b42b5',
    marginBottom: '4px',
    lineHeight: '1'
  };

  const featuresStatLabelStyles = {
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  };

  const featuresCTAStyles = {
    marginTop: '12px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px'
  };

  const featuresCTAButtonStyles = {
    padding: '14px 28px',
    borderRadius: '50px',
    fontSize: '16px',
    fontWeight: '600',
    backgroundColor: '#5b42b5',
    color: '#ffffff',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 10px rgba(91, 66, 181, 0.3)',
    '&:hover': {
      backgroundColor: '#4a35a0',
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 15px rgba(91, 66, 181, 0.4)'
    }
  };

  // Tablet Styles
  const tabletFeaturesTabContentActiveStyles = {
    flexDirection: 'column',
    gap: '40px'
  };

  const tabletFeaturesImageStyles = {
    width: '100%',
    maxWidth: '600px',
    marginBottom: '20px'
  };

  const tabletFeaturesInfoStyles = {
    width: '100%'
  };

  const tabletFeaturesStatsStyles = {
    justifyContent: 'space-between',
    gap: '20px',
    flexWrap: 'wrap'
  };

  // Mobile Styles
  const mobileFeaturesTabsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  };

  const mobileFeaturesTitleStyles = {
    fontSize: '36px'
  };

  const mobileFeaturesCTAStyles = {
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '15px',
    width: '100%'
  };

  const mobileFeaturesCTAButtonStyles = {
    width: '100%',
    textAlign: 'center'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(featuresSectionClass, featuresSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresContainerClass, featuresContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresHeaderClass, featuresHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTitleClass, featuresTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresSubtitleClass, featuresSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresContentClass, featuresContentStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabsClass, featuresTabsStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabClass, featuresTabStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabActiveClass, featuresTabActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabsContentClass, featuresTabsContentStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabContentClass, featuresTabContentStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTabContentActiveClass, featuresTabContentActiveStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresImageClass, featuresImageStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresInfoClass, featuresInfoStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresInfoTitleClass, featuresInfoTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresInfoDescriptionClass, featuresInfoDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresChecklistClass, featuresChecklistStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresChecklistItemClass, featuresChecklistItemStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresCheckIconClass, featuresCheckIconStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresStatsClass, featuresStatsStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresStatClass, featuresStatStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresStatNumberClass, featuresStatNumberStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresStatLabelClass, featuresStatLabelStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresCTAClass, featuresCTAStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresCTAButtonClass, featuresCTAButtonStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(featuresTabContentActiveClass, tabletFeaturesTabContentActiveStyles);
    newRegistry[DEVICES.TABLET].set(featuresImageClass, tabletFeaturesImageStyles);
    newRegistry[DEVICES.TABLET].set(featuresInfoClass, tabletFeaturesInfoStyles);
    newRegistry[DEVICES.TABLET].set(featuresStatsClass, tabletFeaturesStatsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(featuresTabsClass, mobileFeaturesTabsStyles);
    newRegistry[DEVICES.MOBILE].set(featuresTitleClass, mobileFeaturesTitleStyles);
    newRegistry[DEVICES.MOBILE].set(featuresCTAClass, mobileFeaturesCTAStyles);
    newRegistry[DEVICES.MOBILE].set(featuresCTAButtonClass, mobileFeaturesCTAButtonStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    featuresSectionClass,
    featuresContainerClass,
    featuresHeaderClass,
    featuresTitleClass,
    featuresSubtitleClass,
    featuresContentClass,
    featuresTabsClass,
    featuresTabClass,
    featuresTabActiveClass,
    featuresTabsContentClass,
    featuresTabContentClass,
    featuresTabContentActiveClass,
    featuresImageClass,
    featuresInfoClass,
    featuresInfoTitleClass,
    featuresInfoDescriptionClass,
    featuresChecklistClass,
    featuresChecklistItemClass,
    featuresCheckIconClass,
    featuresStatsClass,
    featuresStatClass,
    featuresStatNumberClass,
    featuresStatLabelClass,
    featuresCTAClass,
    featuresCTAButtonClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const contentId = Date.now() + 5;
  
  // Tabs
  const tabsId = Date.now() + 6;
  const tab1Id = Date.now() + 7;
  const tab2Id = Date.now() + 8;
  const tab3Id = Date.now() + 9;
  
  // Tabs content
  const tabsContentId = Date.now() + 10;
  
  // Tab 1 content
  const tab1ContentId = Date.now() + 11;
  const tab1ImageId = Date.now() + 12;
  const tab1InfoId = Date.now() + 13;
  const tab1InfoTitleId = Date.now() + 14;
  const tab1InfoDescriptionId = Date.now() + 15;
  const tab1ChecklistId = Date.now() + 16;
  const tab1ChecklistItem1Id = Date.now() + 17;
  const tab1ChecklistItem2Id = Date.now() + 18;
  const tab1ChecklistItem3Id = Date.now() + 19;
  const tab1CheckIcon1Id = Date.now() + 20;
  const tab1CheckIcon2Id = Date.now() + 21;
  const tab1CheckIcon3Id = Date.now() + 22;
  const tab1StatsId = Date.now() + 23;
  const tab1Stat1Id = Date.now() + 24;
  const tab1Stat1NumberId = Date.now() + 25;
  const tab1Stat1LabelId = Date.now() + 26;
  const tab1Stat2Id = Date.now() + 27;
  const tab1Stat2NumberId = Date.now() + 28;
  const tab1Stat2LabelId = Date.now() + 29;
  const tab1CTAId = Date.now() + 30;
  const tab1CTAButtonId = Date.now() + 31;
  
  // Tab 2 content
  const tab2ContentId = Date.now() + 32;
  const tab2ImageId = Date.now() + 33;
  const tab2InfoId = Date.now() + 34;
  const tab2InfoTitleId = Date.now() + 35;
  const tab2InfoDescriptionId = Date.now() + 36;
  const tab2ChecklistId = Date.now() + 37;
  const tab2ChecklistItem1Id = Date.now() + 38;
  const tab2ChecklistItem2Id = Date.now() + 39;
  const tab2ChecklistItem3Id = Date.now() + 40;
  const tab2CheckIcon1Id = Date.now() + 41;
  const tab2CheckIcon2Id = Date.now() + 42;
  const tab2CheckIcon3Id = Date.now() + 43;
  const tab2StatsId = Date.now() + 44;
  const tab2Stat1Id = Date.now() + 45;
  const tab2Stat1NumberId = Date.now() + 46;
  const tab2Stat1LabelId = Date.now() + 47;
  const tab2Stat2Id = Date.now() + 48;
  const tab2Stat2NumberId = Date.now() + 49;
  const tab2Stat2LabelId = Date.now() + 50;
  const tab2CTAId = Date.now() + 51;
  const tab2CTAButtonId = Date.now() + 52;
  
  // Tab 3 content
  const tab3ContentId = Date.now() + 53;
  const tab3ImageId = Date.now() + 54;
  const tab3InfoId = Date.now() + 55;
  const tab3InfoTitleId = Date.now() + 56;
  const tab3InfoDescriptionId = Date.now() + 57;
  const tab3ChecklistId = Date.now() + 58;
  const tab3ChecklistItem1Id = Date.now() + 59;
  const tab3ChecklistItem2Id = Date.now() + 60;
  const tab3ChecklistItem3Id = Date.now() + 61;
  const tab3CheckIcon1Id = Date.now() + 62;
  const tab3CheckIcon2Id = Date.now() + 63;
  const tab3CheckIcon3Id = Date.now() + 64;
  const tab3StatsId = Date.now() + 65;
  const tab3Stat1Id = Date.now() + 66;
  const tab3Stat1NumberId = Date.now() + 67;
  const tab3Stat1LabelId = Date.now() + 68;
  const tab3Stat2Id = Date.now() + 69;
  const tab3Stat2NumberId = Date.now() + 70;
  const tab3Stat2LabelId = Date.now() + 71;
  const tab3CTAId = Date.now() + 72;
  const tab3CTAButtonId = Date.now() + 73;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Features Section',
      parentId: null,
      className: featuresSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'features-section',
        'data-component': 'section',
        'data-section-type': 'features',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: featuresContainerClass,
      styles: {},
      children: [headerId, contentId],
      text: '',
      attributes: {
        'data-element': 'features-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: featuresHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'features-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Features Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: featuresTitleClass,
      styles: {},
      children: [],
      text: 'Advanced Features for Growth & Success',
      attributes: {
        'data-element': 'features-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Features Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: featuresSubtitleClass,
      styles: {},
      children: [],
      text: 'Discover how our platform can transform your business with powerful tools designed for modern companies. From seamless integrations to advanced analytics, we have everything you need to succeed.',
      attributes: {
        'data-element': 'features-subtitle'
      }
    },
    
    // Content container
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Content',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: featuresContentClass,
      styles: {},
      children: [tabsId, tabsContentId],
      text: '',
      attributes: {
        'data-element': 'features-content'
      }
    },
    
    // Tabs
    {
      id: tabsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Tabs',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabsClass,
      styles: {},
      children: [tab1Id, tab2Id, tab3Id],
      text: '',
      attributes: {
        'data-element': 'features-tabs'
      }
    },
    
    // Tab 1
    {
      id: tab1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 1',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: `${featuresTabClass} ${featuresTabActiveClass}`,
      styles: {},
      children: [],
      text: 'Digital Marketing',
      attributes: {
        'data-element': 'features-tab',
        'data-tab': 'tab-1',
        'data-active': 'true'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1000,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: featuresTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: featuresTabContentActiveClass
            },
            label: `Tab Click: ${featuresTabActiveClass} & ${featuresTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 2
    {
      id: tab2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 2',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabClass,
      styles: {},
      children: [],
      text: 'E-commerce Solutions',
      attributes: {
        'data-element': 'features-tab',
        'data-tab': 'tab-2',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1001,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: featuresTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: featuresTabContentActiveClass
            },
            label: `Tab Click: ${featuresTabActiveClass} & ${featuresTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tab 3
    {
      id: tab3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'tab-button',
      name: 'Tab 3',
      parentId: tabsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabClass,
      styles: {},
      children: [],
      text: 'Analytics Platform',
      attributes: {
        'data-element': 'features-tab',
        'data-tab': 'tab-3',
        'data-active': 'false'
      },
      interactions: {
        events: [
          {
            id: Date.now() + 1002,
            type: 'tab',
            action: 'tabclick',
            value: {
              tabsParentId: tabsId,
              tabButtonClass: featuresTabActiveClass,
              tabsContentParentId: tabsContentId,
              tabContentClass: featuresTabContentActiveClass
            },
            label: `Tab Click: ${featuresTabActiveClass} & ${featuresTabContentActiveClass}`
          }
        ]
      }
    },
    
    // Tabs content container
    {
      id: tabsContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tabs Content',
      parentId: contentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabsContentClass,
      styles: {},
      children: [tab1ContentId, tab2ContentId, tab3ContentId],
      text: '',
      attributes: {
        'data-element': 'features-tabs-content'
      }
    },
    
    // Tab 1 content
    {
      id: tab1ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: `${featuresTabContentClass} ${featuresTabContentActiveClass}`,
      styles: {},
      children: [tab1ImageId, tab1InfoId],
      text: '',
      attributes: {
        'data-element': 'features-tab-content',
        'data-tab-content': 'tab-1',
        'data-active': 'true'
      }
    },
    
    // Tab 1 Image
    {
      id: tab1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Image',
      parentId: tab1ContentId,
      hierarchyName: getHierarchyName('image'),
      className: featuresImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1551434678-e076c223a692?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'features-image',
        'alt': 'Digital Marketing Features'
      }
    },
    
    // Tab 1 Info
    {
      id: tab1InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Info',
      parentId: tab1ContentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresInfoClass,
      styles: {},
      children: [tab1InfoTitleId, tab1InfoDescriptionId, tab1ChecklistId, tab1StatsId, tab1CTAId],
      text: '',
      attributes: {
        'data-element': 'features-info'
      }
    },
    
    // Tab 1 Info Title
    {
      id: tab1InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 1 Info Title',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: featuresInfoTitleClass,
      styles: {},
      children: [],
      text: 'Powerful Digital Marketing Tools',
      attributes: {
        'data-element': 'features-info-title'
      }
    },
    
    // Tab 1 Info Description
    {
      id: tab1InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 1 Info Description',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('p'),
      className: featuresInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Elevate your brand\'s online presence with our comprehensive digital marketing suite. From social media management to email campaigns, we provide everything you need to engage your audience and drive conversions.',
      attributes: {
        'data-element': 'features-info-description'
      }
    },
    
    // Tab 1 Checklist
    {
      id: tab1ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistClass,
      styles: {},
      children: [tab1ChecklistItem1Id, tab1ChecklistItem2Id, tab1ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'features-checklist'
      }
    },
    
    // Tab 1 Checklist Item 1
    {
      id: tab1ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 1',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon1Id],
      text: 'Automated social media posting across all major platforms',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 1 Check Icon 1
    {
      id: tab1CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 1',
      parentId: tab1ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 1 Checklist Item 2
    {
      id: tab1ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 2',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon2Id],
      text: 'Advanced email marketing with AI-powered personalization',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 1 Check Icon 2
    {
      id: tab1CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 2',
      parentId: tab1ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 1 Checklist Item 3
    {
      id: tab1ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Checklist Item 3',
      parentId: tab1ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab1CheckIcon3Id],
      text: 'SEO optimization tools with real-time keyword analysis',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 1 Check Icon 3
    {
      id: tab1CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 1 Check Icon 3',
      parentId: tab1ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 1 Stats
    {
      id: tab1StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stats',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatsClass,
      styles: {},
      children: [tab1Stat1Id, tab1Stat2Id],
      text: '',
      attributes: {
        'data-element': 'features-stats'
      }
    },
    
    // Tab 1 Stat 1
    {
      id: tab1Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1',
      parentId: tab1StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab1Stat1NumberId, tab1Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 1 Stat 1 Number
    {
      id: tab1Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1 Number',
      parentId: tab1Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '63%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 1 Stat 1 Label
    {
      id: tab1Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 1 Label',
      parentId: tab1Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'INCREASED ENGAGEMENT',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 1 Stat 2
    {
      id: tab1Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2',
      parentId: tab1StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab1Stat2NumberId, tab1Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 1 Stat 2 Number
    {
      id: tab1Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2 Number',
      parentId: tab1Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '42%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 1 Stat 2 Label
    {
      id: tab1Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 Stat 2 Label',
      parentId: tab1Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'CONVERSION RATE',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 1 CTA
    {
      id: tab1CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 1 CTA',
      parentId: tab1InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresCTAClass,
      styles: {},
      children: [tab1CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'features-cta'
      }
    },
    
    // Tab 1 CTA Button
    {
      id: tab1CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 1 CTA Button',
      parentId: tab1CTAId,
      hierarchyName: getHierarchyName('button'),
      className: featuresCTAButtonClass,
      styles: {},
      children: [],
      text: 'Get Started with Marketing',
      attributes: {
        'data-element': 'features-cta-button'
      }
    },
    
    // Tab 2 content
    {
      id: tab2ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabContentClass,
      styles: {},
      children: [tab2ImageId, tab2InfoId],
      text: '',
      attributes: {
        'data-element': 'features-tab-content',
        'data-tab-content': 'tab-2',
        'data-active': 'false'
      }
    },
    
    // Tab 2 Image
    {
      id: tab2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Image',
      parentId: tab2ContentId,
      hierarchyName: getHierarchyName('image'),
      className: featuresImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1559526324-593bc073d938?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'features-image',
        'alt': 'E-commerce Solutions'
      }
    },
    
    // Tab 2 Info
    {
      id: tab2InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Info',
      parentId: tab2ContentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresInfoClass,
      styles: {},
      children: [tab2InfoTitleId, tab2InfoDescriptionId, tab2ChecklistId, tab2StatsId, tab2CTAId],
      text: '',
      attributes: {
        'data-element': 'features-info'
      }
    },
    
    // Tab 2 Info Title
    {
      id: tab2InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 2 Info Title',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: featuresInfoTitleClass,
      styles: {},
      children: [],
      text: 'Complete E-commerce Solutions',
      attributes: {
        'data-element': 'features-info-title'
      }
    },
    
    // Tab 2 Info Description
    {
      id: tab2InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 2 Info Description',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('p'),
      className: featuresInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Transform your business with our end-to-end e-commerce platform. From product listings to checkout and payment processing, we provide a seamless shopping experience for your customers.',
      attributes: {
        'data-element': 'features-info-description'
      }
    },
    
    // Tab 2 Checklist
    {
      id: tab2ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistClass,
      styles: {},
      children: [tab2ChecklistItem1Id, tab2ChecklistItem2Id, tab2ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'features-checklist'
      }
    },
    
    // Tab 2 Checklist Item 1
    {
      id: tab2ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 1',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon1Id],
      text: 'Secure payment processing with multiple gateway options',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 2 Check Icon 1
    {
      id: tab2CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 1',
      parentId: tab2ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 2 Checklist Item 2
    {
      id: tab2ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 2',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon2Id],
      text: 'Inventory management with automatic stock updates',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 2 Check Icon 2
    {
      id: tab2CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 2',
      parentId: tab2ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 2 Checklist Item 3
    {
      id: tab2ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Checklist Item 3',
      parentId: tab2ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab2CheckIcon3Id],
      text: 'Customer accounts with order history and saved preferences',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 2 Check Icon 3
    {
      id: tab2CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 2 Check Icon 3',
      parentId: tab2ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 2 Stats
    {
      id: tab2StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stats',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatsClass,
      styles: {},
      children: [tab2Stat1Id, tab2Stat2Id],
      text: '',
      attributes: {
        'data-element': 'features-stats'
      }
    },
    
    // Tab 2 Stat 1
    {
      id: tab2Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1',
      parentId: tab2StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab2Stat1NumberId, tab2Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 2 Stat 1 Number
    {
      id: tab2Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1 Number',
      parentId: tab2Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '85%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 2 Stat 1 Label
    {
      id: tab2Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 1 Label',
      parentId: tab2Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'CHECKOUT COMPLETION',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 2 Stat 2
    {
      id: tab2Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2',
      parentId: tab2StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab2Stat2NumberId, tab2Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 2 Stat 2 Number
    {
      id: tab2Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2 Number',
      parentId: tab2Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '24%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 2 Stat 2 Label
    {
      id: tab2Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 Stat 2 Label',
      parentId: tab2Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'INCREASE IN SALES',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 2 CTA
    {
      id: tab2CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 2 CTA',
      parentId: tab2InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresCTAClass,
      styles: {},
      children: [tab2CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'features-cta'
      }
    },
    
    // Tab 2 CTA Button
    {
      id: tab2CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 2 CTA Button',
      parentId: tab2CTAId,
      hierarchyName: getHierarchyName('button'),
      className: featuresCTAButtonClass,
      styles: {},
      children: [],
      text: 'Launch Your Store',
      attributes: {
        'data-element': 'features-cta-button'
      }
    },
    
    // Tab 3 content
    {
      id: tab3ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Content',
      parentId: tabsContentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresTabContentClass,
      styles: {},
      children: [tab3ImageId, tab3InfoId],
      text: '',
      attributes: {
        'data-element': 'features-tab-content',
        'data-tab-content': 'tab-3',
        'data-active': 'false'
      }
    },
    
    // Tab 3 Image
    {
      id: tab3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Image',
      parentId: tab3ContentId,
      hierarchyName: getHierarchyName('image'),
      className: featuresImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1559526324-593bc073d938?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80',
      attributes: {
        'data-element': 'features-image',
        'alt': 'Analytics Platform'
      }
    },
    
    // Tab 3 Info
    {
      id: tab3InfoId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Info',
      parentId: tab3ContentId,
      hierarchyName: getHierarchyName('div'),
      className: featuresInfoClass,
      styles: {},
      children: [tab3InfoTitleId, tab3InfoDescriptionId, tab3ChecklistId, tab3StatsId, tab3CTAId],
      text: '',
      attributes: {
        'data-element': 'features-info'
      }
    },
    
    // Tab 3 Info Title
    {
      id: tab3InfoTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Tab 3 Info Title',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('h3'),
      className: featuresInfoTitleClass,
      styles: {},
      children: [],
      text: 'Advanced Analytics Platform',
      attributes: {
        'data-element': 'features-info-title'
      }
    },
    
    // Tab 3 Info Description
    {
      id: tab3InfoDescriptionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Tab 3 Info Description',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('p'),
      className: featuresInfoDescriptionClass,
      styles: {},
      children: [],
      text: 'Gain insights and make data-driven decisions with our advanced analytics platform. From customer behavior analysis to predictive modeling, we provide actionable insights to drive your business forward.',
      attributes: {
        'data-element': 'features-info-description'
      }
    },
    
    // Tab 3 Checklist
    {
      id: tab3ChecklistId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistClass,
      styles: {},
      children: [tab3ChecklistItem1Id, tab3ChecklistItem2Id, tab3ChecklistItem3Id],
      text: '',
      attributes: {
        'data-element': 'features-checklist'
      }
    },
    
    // Tab 3 Checklist Item 1
    {
      id: tab3ChecklistItem1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 1',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon1Id],
      text: 'Real-time data visualization with interactive dashboards',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 3 Check Icon 1
    {
      id: tab3CheckIcon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 1',
      parentId: tab3ChecklistItem1Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 3 Checklist Item 2
    {
      id: tab3ChecklistItem2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 2',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon2Id],
      text: 'Advanced predictive modeling with machine learning',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 3 Check Icon 2
    {
      id: tab3CheckIcon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 2',
      parentId: tab3ChecklistItem2Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 3 Checklist Item 3
    {
      id: tab3ChecklistItem3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Checklist Item 3',
      parentId: tab3ChecklistId,
      hierarchyName: getHierarchyName('div'),
      className: featuresChecklistItemClass,
      styles: {},
      children: [tab3CheckIcon3Id],
      text: 'Customizable reporting with exportable data',
      attributes: {
        'data-element': 'features-checklist-item'
      }
    },
    
    // Tab 3 Check Icon 3
    {
      id: tab3CheckIcon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Tab 3 Check Icon 3',
      parentId: tab3ChecklistItem3Id,
      hierarchyName: getHierarchyName('image'),
      className: featuresCheckIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:check-circle-rounded.svg?color=%235b42b5&width=20&height=20',
      attributes: {
        'data-element': 'features-check-icon',
        'alt': 'Check Icon'
      }
    },
    
    // Tab 3 Stats
    {
      id: tab3StatsId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stats',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatsClass,
      styles: {},
      children: [tab3Stat1Id, tab3Stat2Id],
      text: '',
      attributes: {
        'data-element': 'features-stats'
      }
    },
    
    // Tab 3 Stat 1
    {
      id: tab3Stat1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1',
      parentId: tab3StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab3Stat1NumberId, tab3Stat1LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 3 Stat 1 Number
    {
      id: tab3Stat1NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1 Number',
      parentId: tab3Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '75%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 3 Stat 1 Label
    {
      id: tab3Stat1LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 1 Label',
      parentId: tab3Stat1Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'INCREASED CONVERSION',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 3 Stat 2
    {
      id: tab3Stat2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2',
      parentId: tab3StatsId,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatClass,
      styles: {},
      children: [tab3Stat2NumberId, tab3Stat2LabelId],
      text: '',
      attributes: {
        'data-element': 'features-stat'
      }
    },
    
    // Tab 3 Stat 2 Number
    {
      id: tab3Stat2NumberId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2 Number',
      parentId: tab3Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatNumberClass,
      styles: {},
      children: [],
      text: '50%',
      attributes: {
        'data-element': 'features-stat-number'
      }
    },
    
    // Tab 3 Stat 2 Label
    {
      id: tab3Stat2LabelId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 Stat 2 Label',
      parentId: tab3Stat2Id,
      hierarchyName: getHierarchyName('div'),
      className: featuresStatLabelClass,
      styles: {},
      children: [],
      text: 'INCREASED REVENUE',
      attributes: {
        'data-element': 'features-stat-label'
      }
    },
    
    // Tab 3 CTA
    {
      id: tab3CTAId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Tab 3 CTA',
      parentId: tab3InfoId,
      hierarchyName: getHierarchyName('div'),
      className: featuresCTAClass,
      styles: {},
      children: [tab3CTAButtonId],
      text: '',
      attributes: {
        'data-element': 'features-cta'
      }
    },
    
    // Tab 3 CTA Button
    {
      id: tab3CTAButtonId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'button',
      name: 'Tab 3 CTA Button',
      parentId: tab3CTAId,
      hierarchyName: getHierarchyName('button'),
      className: featuresCTAButtonClass,
      styles: {},
      children: [],
      text: 'Get Started with Analytics',
      attributes: {
        'data-element': 'features-cta-button'
      }
    }
  ];

  return {
    featuresSection: newElements[0],
    elements: newElements
  };
};

export default createFeaturesElements; 